import { ButtonConfig, IButtonConfig } from '../../../shared/components/list-view/list-view-button-config.model';
import { IListItem } from '../../../shared/interfaces/ilist-item.interface';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { FormInstance } from '../../../shared/models/site-content/form-instance.model';

// Define/export constant string values.
export const FOLDER: string = 'folder';
export const FORM_INSTANCE: string = 'form-instance';
export const VIEW: string = 'view';

export const SAVE_FORM_INSTANCE: string = 'save_form_instance';
export const RESET_FORM_INSTANCE: string = 'reset_form_instance';
export const PUBLISH: string = 'publish';
export const PRINT_FORM_INSTANCE: string = 'print_form_instance';
export const BULK_WORKFLOW = 'bulk_workflow';
export const HISTORY: string = 'history';
export const SUBSCRIBE: string = 'subscribe';
export const IMPORT_FORM_INSTANCE_DATA: string = 'import_form_instance_data';
export const IMPORT_FORM_INSTANCE_FILES: string = 'import_form_instance_files';
export const EXPORT_FORM_INSTANCE_FILES: string = 'export_form_instance_files';
export const EXPORT_TO_EXCEL: string = 'export_to_excel';
export const FORM: string = 'form';
export const FORM_DETAILS: string = 'form_details';
export const UP_ONE_LEVEL: string = 'up';
export const TREE: string = 'tree';
export const INFO: string = 'info';
export const ROLES: string = 'roles';
export const FAVORITE: string = 'favorite';

export const TRUE: string = 'true';
export const FALSE: string = 'false';

// Define two type-safe hash/map/dictionary types.
interface IFolders {
    [folderId: number]: Folder;
}
interface IFavoriteFormInstances {
    [formInstance: number]: FormInstance;
}

// Defines the ButtonConfig for the action button on a Folder list item which opens a Folder's Roles Viewer
// When a Folder inherits its roles, the button icon has the usual look,
// when a Folder manages its own roles, the button icon is filled in with max dark blue
export class ViewAndManageRolesButtonConfig extends ButtonConfig implements IButtonConfig {
    private selfManagedFolders: IFolders = {};
    private foldersWithDisabledRoleViewer: IFolders = {};
    private iconWhenFolderManagesOwnRoles: string;
    private iconWhenRoleViewerNotAccessible: string;

    // the constructor takes a ButtonConfig and the name of the  icon to use when a Folder is managing its own role
    public constructor(
        buttonConfig: ButtonConfig,
        iconWhenFolderManagesOwnRoles: string,
        iconWhenRoleViewerNotAccessible: string,
        childFolders: Folder[]
    ) {
        super(buttonConfig.label, buttonConfig.value, buttonConfig.icon, buttonConfig.iconFormat);
        this.iconWhenFolderManagesOwnRoles = iconWhenFolderManagesOwnRoles;
        this.iconWhenRoleViewerNotAccessible = iconWhenRoleViewerNotAccessible;

        for (let iFolder: number = 0; iFolder < childFolders.length; iFolder++) {
            let folder: Folder = childFolders[iFolder];

            if (!folder.roleMembersAreInherited) {
                this.selfManagedFolders[folder.id] = folder;
            }
            if (!folder.canGrant) {
                this.foldersWithDisabledRoleViewer[folder.id] = folder;
            }
        }
    }

    // the role button only applies to folders
    public appliesTo(item: IListItem): boolean {
        return item.getType() == FOLDER;
    }

    // overide 
    // selects between the default dark grey button
    // a filled-in blue one when the Folder manages its own Roles
    // and a light greyed-out one when the user doesn't have permission to view Roles
    public iconFor(folderListItem: IListItem): string {
        let iconName: string = this.icon;  // set the default

        if (this.userCannotViewRoles(folderListItem)) {
            iconName = this.iconWhenRoleViewerNotAccessible;
        } else if (this.managesOwnRoles(folderListItem)) {
            iconName = this.iconWhenFolderManagesOwnRoles;
        }
        return iconName;
    }

    // overide 
    public isDisabled(folderListItem: IListItem) {
        return this.userCannotViewRoles(folderListItem);
    }

    public updateSelfManagedFolders(folder: Folder): void {
        if (folder.roleMembersAreInherited) {
            delete this.selfManagedFolders[folder.id];
        } else {
            this.selfManagedFolders[folder.id] = folder;
        }
    }

    private managesOwnRoles(item: IListItem) {
        return this.selfManagedFolders[item.getId()] != null;
    }

    private userCannotViewRoles(item: IListItem) {
        return this.foldersWithDisabledRoleViewer[item.getId()] != null;
    }
}

// Defines the ButtonConfig for the action button on a Folder/FormInstance list item which opens a Item's "set Favorite" drawer
// When an Item has been Faved, it is displays as solid, colored star
// When an Item has not been Faved, it displays as a non-filled star
export class FoldersFormInstancesFavoritesButtonConfig extends ButtonConfig implements IButtonConfig {
    // Properties.
    private isFavoriteIcon: string = null;

    // Favorite child folders of the current folder.
    private hshFavoriteFolders: IFolders = {};
    // Favorite form instances of the current folder.
    private hshFavoriteFormInstances: IFavoriteFormInstances = {};

    // Constructor.
    public constructor(
        buttonConfig: ButtonConfig,
        iconWhenIsFavorite: string,
        folders: Folder[],
        formInstances: FormInstance[]) {
        super(buttonConfig.label,
            buttonConfig.value,
            buttonConfig.icon,
            buttonConfig.iconFormat);

        this.isFavoriteIcon = iconWhenIsFavorite;

        this.setCurrentFolderContents(folders ?? [], formInstances ?? []);
        return;
    }

    // Methods for managing favorite statuses.
    public updateFolderFavoriteStatus(folder: Folder): void {
        if (folder.isFavorite) {
            // Need to add this folder to the hash of favorites.
            if (!this.hshFavoriteFolders[folder.id]) {
                this.hshFavoriteFolders[folder.id] = folder;
            }
        } else {
            // Need to remove this folder from the hash of favorites.
            if (this.hshFavoriteFolders[folder.id]) {
                delete this.hshFavoriteFolders[folder.id];
            }
        }

        return;
    }

    public updateFormInstanceFavoriteStatus(formInstance: FormInstance): void {
        if (formInstance.isFavorite) {
            // Need to add this folder to the hash of favorites.
            if (!this.hshFavoriteFormInstances[formInstance.id]) {
                this.hshFavoriteFormInstances[formInstance.id] = formInstance;
            }
        } else {
            // Need to remove this folder from the hash of favorites.
            if (this.hshFavoriteFormInstances[formInstance.id]) {
                delete this.hshFavoriteFormInstances[formInstance.id];
            }
        }

        return;
    }

    // Override the base class implementation
    // of the iconForm() method.
    public iconFor(item: IListItem): string { // Returns the icon name for the specified item.
        let iconName: string = this.icon;
        let bIsFavorite: boolean = false;

        if (item.getType() == FOLDER) {
            bIsFavorite = ((this.hshFavoriteFolders[item.getId()] !== undefined) && (this.hshFavoriteFolders[item.getId()] !== null));
        } else if (item.getType() == FORM_INSTANCE) {
            bIsFavorite = ((this.hshFavoriteFormInstances[item.getId()] !== undefined) && (this.hshFavoriteFormInstances[item.getId()] !== null));
        }

        if (bIsFavorite) {
            iconName = this.isFavoriteIcon;
        }

        return (iconName);
    }

    // Helper method -- for managing the current folder's content.
    private setCurrentFolderContents(folders: Folder[], formInstances: FormInstance[]): void {
        this.hshFavoriteFolders = {};
        this.hshFavoriteFormInstances = {};

        for (let iFolder: number = 0; iFolder < folders.length; iFolder++) {
            let folder: Folder = folders[iFolder];

            if (folder.isFavorite) {
                this.hshFavoriteFolders[folder.id] = folder;
            }
        }

        for (let iFI: number = 0; iFI < formInstances.length; iFI++) {
            let formInstance: FormInstance = formInstances[iFI];

            if (formInstance.isFavorite) {
                this.hshFavoriteFormInstances[formInstance.id] = formInstance;
            }
        }

        return;
    }

}
